import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Typography, Box, IconButton, Button,
  Tooltip, Avatar, Chip, Card, CardContent, alpha, Link, useTheme,
} from '@mui/material';
import {
  Edit as EditIcon, Archive as ArchiveIcon, Restore as RestoreIcon, Add as AddIcon,
  Download as DownloadIcon, UploadFile as UploadFileIcon,
  Business as BusinessIcon, Assessment as AssessmentIcon,
  Person as PersonIcon, Email as EmailIcon, Phone as PhoneIcon,
  Category as CategoryIcon, Star as StarIcon,
  CheckCircle as ActiveIcon, HourglassEmpty as PendingIcon,
  Visibility as VisibilityIcon, ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import FilterBar from '../../common/FilterBar';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import StatCards from '../../common/StatCards';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { COMPANY_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';

import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import StorageAvatar from '../../common/StorageAvatar';
import { canAccessPartnershipManagement, canDeleteCompany, companyHasDeleteBlockers } from '../../../utils/partnershipPermissions';
import { motion } from 'framer-motion';
import { SUCCESS, glassCardSx } from '../../common/SaaSTokens';

// ─── Design tokens — cyberpunk dark system ────────────────────────────────────
const B = { 600: '#2563EB', 700: '#1D4ED8', 900: '#1E3A8A' };
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';
const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

const DOT_GRID   = `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='0.55' fill='%2338BDF8' fill-opacity='0.07'/%3E%3C/svg%3E")`;
const SCAN_LINES = `repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(14,165,233,0.016) 3px, rgba(14,165,233,0.016) 4px)`;

const CL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes bar-sweep { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
`;

const MotionCard = motion(Card);

const cardShellSx = glassCardSx;

const getStatusNeon = (s) => ({
  follow_up: '#FCD34D', active: NEON3, inactive: '#F87171', archived: '#94A3B8', pending: '#94A3B8',
}[s] || alpha(NEON, 0.5));

const companyDisplayStatus = (company) => company?.archived_at ? 'archived' : company?.status;

const companyHasPartner = (company) => companyHasDeleteBlockers(company);

const getRepresentativeId = (company) =>
  company?.representative_user_id ?? company?.representative?.id ?? null;

const CompanyList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();
  
  const hasPartnershipAccess = canAccessPartnershipManagement(currentUser);

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const fileInputRef = useRef(null);

  const canManageCompanies = hasPartnershipAccess;

  const filterOptions = [
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      searchable: true,
      options: [
        { value: 'follow_up', label: 'Follow up' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'archived', label: 'Archived' },
      ],
    },
    {
      name: 'representative_user_id',
      label: 'Assigned to',
      type: 'select',
      searchable: true,
      options: userOptions.map((user) => ({
        value: String(user.id),
        label: user.name || user.email || `#${user.id}`,
      })),
    },
    {
      name: 'is_partner',
      label: 'Is Partner (Sponsor)',
      type: 'select',
      searchable: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
    },
  ];

  useEffect(() => {
    const cssId = 'companylist-ds-css';
    if (!document.getElementById(cssId)) { const el = document.createElement('style'); el.id = cssId; el.innerHTML = CL_CSS; document.head.appendChild(el); }
  }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    if (!hasPartnershipAccess) { navigate('/dashboard'); return; }
    fetchCompanies({ forceRefresh: Boolean(location.state?.refreshList) });
    loadFilterOptions();
  }, [hasPartnershipAccess, currentUser, location.pathname, location.state?.refreshList]);

  const loadFilterOptions = async () => {
    try {
      const usersRes = await api.get('/users?active_only=1', { cache: false });
      setUserOptions(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch {
      setUserOptions([]);
    }
  };

  const fetchCompanies = async ({ forceRefresh = false, archived = false } = {}) => {
    try {
      setLoading(true);
      const r = await api.get('/companies', {
        ...(forceRefresh ? { cache: false } : {}),
        ...(archived ? { params: { archived: 1 } } : {}),
      });
      setCompanies(r.data); setError(null);
    } catch { setError('Failed to load companies. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleRestore = async (company) => {
    try {
      await api.post(`/companies/${company.id}/restore`);
      setCompanies((prev) => prev.filter((item) => item.id !== company.id));
      setSuccess(`Company "${company.name || 'Company'}" has been restored successfully!`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to restore company. Please try again.');
    }
  };

  const handleDelete = (company) => { setSelectedCompany(company); setDeleteDialogOpen(true); };
  const confirmDelete = async () => {
    const companyName = selectedCompany?.name || 'Company';
    try {
      setDeleteLoading(true);
      await api.post(`/companies/${selectedCompany.id}/archive`);
      setCompanies((prev) => prev.filter((company) => company.id !== selectedCompany.id));
      setDeleteDialogOpen(false);
      setSelectedCompany(null);
      setSuccess(`Company "${companyName}" has been archived successfully!`);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to archive company. Please try again.');
    }
    finally { setDeleteLoading(false); }
  };

  const handleBulkArchive = async () => {
    const ids = [...(selectedIds || [])];
    if (!ids.length) {
      setError('No selected companies to archive.');
      return;
    }

    try {
      await Promise.all(ids.map((companyId) => api.post(`/companies/${Number(companyId)}/archive`)));
      const idSet = new Set(ids.map(String));
      setCompanies((prev) => prev.filter((company) => !idSet.has(String(company.id))));
      setActiveStatFilter('all');
      setSearchTerm('');
      setPage(0);
      clearSelection();
      setError(null);
      setSuccess(`${ids.length} company(s) archived successfully.`);
      await fetchCompanies({ forceRefresh: true });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to archive selected companies. Please try again.');
      throw err;
    }
  };

  const handleFilterChange = (newFilters) => {
    const archivedViewChanged = (filters.status === 'archived') !== (newFilters.status === 'archived');
    let nextActive = 'all';
    if (newFilters.status === 'active') nextActive = 'active';
    else if (newFilters.status === 'follow_up') nextActive = 'follow_up';
    else if (newFilters.is_partner === 'yes') nextActive = 'has_partner';
    setActiveStatFilter(nextActive);
    setFilters(newFilters);
    setPage(0);
    if (archivedViewChanged) {
      clearSelection();
      fetchCompanies({ forceRefresh: true, archived: newFilters.status === 'archived' });
    }
  };

  const handleClearFilters = () => {
    const wasArchived = filters.status === 'archived';
    setFilters({});
    setActiveStatFilter('all');
    setSearchTerm('');
    setPage(0);
    if (wasArchived) fetchCompanies({ forceRefresh: true });
  };

  const parseCsvContent = (content) => {
    const rows = [];
    let current = '';
    let row = [];
    let inQuotes = false;

    for (let i = 0; i < content.length; i++) {
      const char = content[i];
      const nextChar = content[i + 1];

      if (char === '"' && inQuotes && nextChar === '"') {
        current += '"';
        i++;
        continue;
      }

      if (char === '"') {
        inQuotes = !inQuotes;
        continue;
      }

      if (char === ',' && !inQuotes) {
        row.push(current.trim());
        current = '';
        continue;
      }

      if ((char === '\n' || char === '\r') && !inQuotes) {
        if (char === '\r' && nextChar === '\n') {
          i++;
        }
        row.push(current.trim());
        if (row.some((cell) => cell !== '')) {
          rows.push(row);
        }
        row = [];
        current = '';
        continue;
      }

      current += char;
    }

    if (current !== '' || row.length > 0) {
      row.push(current.trim());
      if (row.some((cell) => cell !== '')) {
        rows.push(row);
      }
    }

    return rows;
  };

  const normalizeHeader = (header = '') =>
    header.toString().trim().toLowerCase().replace(/[\s-]+/g, '_');

  const toNullableString = (value) => {
    if (value === null || value === undefined) return null;
    const trimmed = String(value).trim();
    return trimmed ? trimmed : null;
  };

  const getCellByHeaders = (rowObj, headers) => {
    for (const header of headers) {
      const key = normalizeHeader(header);
      if (Object.prototype.hasOwnProperty.call(rowObj, key)) {
        return rowObj[key];
      }
    }
    return undefined;
  };

  const buildUserLookupMap = (users) => {
    const map = new Map();
    (users || []).forEach((user) => {
      if (!user?.id) return;
      map.set(String(user.id), user.id);
      const name = String(user.name || '').trim().toLowerCase();
      if (name) map.set(name, user.id);
      const email = String(user.email || '').trim().toLowerCase();
      if (email) map.set(email, user.id);
    });
    return map;
  };

  const resolveUserIdFromValue = (value, lookupMap) => {
    if (value === undefined || value === null || value === '') return null;
    const raw = String(value).trim();
    if (!raw) return null;
    return lookupMap.get(raw.toLowerCase()) ?? lookupMap.get(raw) ?? null;
  };

  const normalizeCompanyStatus = (value) => {
    const raw = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (raw === 'follow_up' || raw === 'followup') return 'follow_up';
    if (raw === 'active') return 'active';
    if (raw === 'inactive') return 'inactive';
    return null;
  };

  const handleDownloadTemplate = async () => {
    try {
      setTemplateLoading(true);
      setError(null);

      const usersRes = await api.get('/users?active_only=1', { cache: false });
      const users = Array.isArray(usersRes.data) ? usersRes.data : [];
      const defaultRep = users.find((user) => user.id === currentUser?.id) ?? users[0];
      const representativeId = defaultRep?.id ?? '';

      const companyHeaders = [
        'name',
        'contact_number',
        'gmail',
        'representative_id',
        'status',
        'website_link',
        'description',
        'location',
        'person_in_charge_name',
        'person_in_charge_email',
        'person_in_charge_phone',
      ];

      const companySample = [
        'Acme Robotics Sdn Bhd',
        '+60123456789',
        'contact@acme.example.com',
        representativeId,
        'follow_up',
        'https://acme.example.com',
        'Potential sponsor for competition season',
        'Kuala Lumpur',
        'Jane Smith',
        'jane.smith@acme.example.com',
        '+60198765432',
      ];

      const xlsx = await import('xlsx');
      const companiesSheet = xlsx.utils.aoa_to_sheet([companyHeaders, companySample]);
      const usersSheet = xlsx.utils.aoa_to_sheet([
        ['id', 'name', 'email'],
        ...users.map((user) => [user.id, user.name || '', user.email || '']),
      ]);

      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, companiesSheet, 'Companies');
      xlsx.utils.book_append_sheet(workbook, usersSheet, 'Users');
      xlsx.writeFile(workbook, 'company_import_template.xlsx');
    } catch (templateError) {
      setError(templateError.message || 'Failed to download company import template.');
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleImportCompaniesClick = () => {
    if (importLoading) return;
    fileInputRef.current?.click();
  };

  const handleImportCompaniesFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImportLoading(true);
      setError(null);
      setSuccess(null);

      const extension = file.name.toLowerCase().split('.').pop();
      let matrix = [];

      if (extension === 'csv') {
        const text = await file.text();
        matrix = parseCsvContent(text);
      } else if (extension === 'xlsx' || extension === 'xls') {
        const xlsx = await import('xlsx');
        const data = await file.arrayBuffer();
        const workbook = xlsx.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
          throw new Error('The uploaded Excel file has no sheets.');
        }
        const sheet = workbook.Sheets[firstSheetName];
        matrix = xlsx.utils.sheet_to_json(sheet, {
          header: 1,
          raw: false,
          defval: '',
        });
      } else {
        throw new Error('Unsupported file format. Please use CSV, XLSX, or XLS.');
      }

      if (!matrix.length || matrix.length < 2) {
        throw new Error('File is empty or missing data rows.');
      }

      const [rawHeaders, ...rawRows] = matrix;
      const headers = rawHeaders.map((header) => normalizeHeader(header));
      const rowObjects = rawRows
        .filter((row) => row.some((cell) => String(cell || '').trim() !== ''))
        .map((row) => {
          const obj = {};
          headers.forEach((header, idx) => {
            obj[header] = row[idx];
          });
          return obj;
        });

      if (!rowObjects.length) {
        throw new Error('No valid data rows found in the uploaded file.');
      }

      const usersRes = await api.get('/users?active_only=1', { cache: false });
      const userMap = buildUserLookupMap(usersRes.data);

      const errors = [];
      let createdCount = 0;

      for (let index = 0; index < rowObjects.length; index++) {
        const rowObj = rowObjects[index];
        const rowNumber = index + 2;

        const name = toNullableString(getCellByHeaders(rowObj, ['name', 'company_name']));
        const contactNumber = toNullableString(getCellByHeaders(rowObj, ['contact_number', 'phone', 'contact']));
        const gmail = toNullableString(getCellByHeaders(rowObj, ['gmail', 'email', 'company_email']));
        const representativeInput = getCellByHeaders(rowObj, [
          'representative_id',
          'representative',
          'representative_email',
          'representative_name',
          'contact_person',
        ]);
        const statusInput = getCellByHeaders(rowObj, ['status']);

        if (!name || !contactNumber || !gmail) {
          errors.push(`Row ${rowNumber}: name, contact_number, and gmail are required.`);
          continue;
        }

        let representativeId = resolveUserIdFromValue(representativeInput, userMap);
        if (representativeId === null && !toNullableString(representativeInput) && currentUser?.id) {
          representativeId = currentUser.id;
        }
        if (representativeId === null) {
          errors.push(`Row ${rowNumber}: representative_id is required. Use a user ID from the Users sheet in the template.`);
          continue;
        }

        const status = normalizeCompanyStatus(statusInput);
        if (!status) {
          errors.push(`Row ${rowNumber}: status must be follow_up, active, or inactive.`);
          continue;
        }

        const payload = {
          name,
          contact_number: contactNumber,
          gmail,
          representative_id: representativeId,
          status,
          website_link: toNullableString(getCellByHeaders(rowObj, ['website_link', 'website'])),
          description: toNullableString(getCellByHeaders(rowObj, ['description'])),
          location: toNullableString(getCellByHeaders(rowObj, ['location', 'address'])),
          person_in_charge_name: toNullableString(getCellByHeaders(rowObj, ['person_in_charge_name', 'pic_name'])),
          person_in_charge_email: toNullableString(getCellByHeaders(rowObj, ['person_in_charge_email', 'pic_email'])),
          person_in_charge_phone: toNullableString(getCellByHeaders(rowObj, ['person_in_charge_phone', 'pic_phone'])),
          facebook_path: toNullableString(getCellByHeaders(rowObj, ['facebook_path', 'facebook'])),
          ig_path: toNullableString(getCellByHeaders(rowObj, ['ig_path', 'instagram'])),
          youtube_path: toNullableString(getCellByHeaders(rowObj, ['youtube_path', 'youtube'])),
          other_path: toNullableString(getCellByHeaders(rowObj, ['other_path', 'other_link'])),
        };

        try {
          await api.post('/companies', payload);
          createdCount++;
        } catch (createError) {
          const backendMessage = createError.response?.data?.message;
          const validationErrors = createError.response?.data?.errors;
          const firstValidationError = validationErrors
            ? Object.values(validationErrors).flat()?.[0]
            : null;
          const message = firstValidationError || backendMessage || createError.message || 'Failed to create company.';
          errors.push(`Row ${rowNumber}: ${message}`);
        }
      }

      await fetchCompanies({ forceRefresh: true });

      if (createdCount > 0 && errors.length === 0) {
        setSuccess(`Successfully imported ${createdCount} company(ies).`);
      } else if (createdCount > 0 && errors.length > 0) {
        setSuccess(`Imported ${createdCount} company(ies), but ${errors.length} row(s) failed.`);
        setError(errors.slice(0, 6).join(' '));
      } else {
        setError(`No companies were imported. ${errors.slice(0, 6).join(' ')}`);
      }
    } catch (importError) {
      setError(importError.message || 'Failed to import companies from file.');
    } finally {
      setImportLoading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'active', 'follow_up', 'has_partner'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);

    const nextFilters = { ...filters };
    delete nextFilters.status;
    delete nextFilters.is_partner;

    if (nextFilter === 'active' || nextFilter === 'follow_up') {
      nextFilters.status = nextFilter;
    } else if (nextFilter === 'has_partner') {
      nextFilters.is_partner = 'yes';
    }

    setFilters(nextFilters);
    setPage(0);
  };

  const activeStatIndex =
    activeStatFilter === 'active' ? 1 :
    activeStatFilter === 'follow_up' ? 2 :
    activeStatFilter === 'has_partner' ? 3 :
    0;

  const filteredCompanies = companies.filter(company => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      company.name.toLowerCase().includes(q) ||
      company.description?.toLowerCase().includes(q) ||
      company.contact_person?.toLowerCase().includes(q) ||
      company.gmail?.toLowerCase().includes(q) ||
      company.representative?.name?.toLowerCase().includes(q) ||
      (company.assignees || []).some((member) => member.name?.toLowerCase().includes(q));
    const matchesFilters = Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      if (key === 'status') return companyDisplayStatus(company) === value;
      if (key === 'representative_user_id') {
        return String(getRepresentativeId(company) ?? '') === String(value);
      }
      if (key === 'is_partner') {
        const hasPartner = companyHasPartner(company);
        if (value === 'yes') return hasPartner;
        if (value === 'no') return !hasPartner;
      }
      return true;
    });
    return matchesSearch && matchesFilters;
  });

  const sortedCompanies = [...filteredCompanies].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );

  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const statsData = [
    { icon: BusinessIcon, value: companies.length, label: 'Total Companies', color: 'info' },
    { icon: PendingIcon, value: companies.filter((c) => c.status === 'follow_up').length, label: 'Follow up', color: 'success' },
    { icon: ActiveIcon, value: companies.filter((c) => c.status === 'active').length, label: 'Active', color: 'warning' },
    { icon: StarIcon, value: companies.filter(companyHasPartner).length, label: 'Partners / Sponsors', color: 'primary' },
  ];

  const getCompanyInitials = (name) => name.split(' ').map(w => w.charAt(0)).join('').toUpperCase().slice(0, 2);

  const columns = [
    {
      key: 'name', label: 'Company', icon: BusinessIcon, minWidth: 280,
      render: (company) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StorageAvatar
            storagePath={company.company_logo}
            sx={{
              width: 40,
              height: 40,
              background: `linear-gradient(135deg, ${alpha(NEON, 0.3)}, ${alpha(B[700], 0.6)})`,
              border: `2px solid ${alpha(NEON, 0.3)}`,
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700,
              fontSize: '0.95rem',
              color: '#fff',
            }}
          >
            {getCompanyInitials(company.name)}
          </StorageAvatar>
          <Box>
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.875rem', color: 'rgba(226,232,240,0.9)' }}>{company.name}</Typography>
            {company.website_link && (
              <Link href={company.website_link} target="_blank" rel="noopener noreferrer" sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.65rem', color: alpha(NEON, 0.55), '&:hover': { color: NEON } }}>
                {company.website_link.replace(/^https?:\/\//, '')}
              </Link>
            )}
          </Box>
        </Box>
      ),
    },
    {
      key: 'contact_person', label: 'Contact Person', icon: PersonIcon, minWidth: 160,
      sortAccessor: (c) => (c.assignees || []).map((member) => member.name).join(', ') || c.representative?.name || c.contact_person || '',
      render: (c) => <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: 'rgba(226,232,240,0.8)' }}>{(c.assignees || []).map((member) => member.name).join(', ') || c.representative?.name || c.contact_person || '—'}</Typography>,
    },
    {
      key: 'gmail', label: 'Email', icon: EmailIcon, minWidth: 180,
      render: (c) => <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: 'rgba(226,232,240,0.75)' }}>{c.gmail || '—'}</Typography>,
    },
    {
      key: 'contact_number', label: 'Phone', icon: PhoneIcon, minWidth: 140,
      render: (c) => <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: 'rgba(226,232,240,0.75)' }}>{c.contact_number || '—'}</Typography>,
    },
    {
      key: 'status', label: 'Status', minWidth: 120,
      render: (company) => {
        const displayStatus = companyDisplayStatus(company);
        const neon = getStatusNeon(displayStatus);
        const label = displayStatus ? displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1) : 'Unknown';
        return (
          <Chip label={label} size="small"
            sx={{ height: 22, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', fontWeight: 600,
              bgcolor: alpha(neon, 0.1), color: neon, border: `1px solid ${alpha(neon, 0.35)}`, boxShadow: `0 0 8px ${alpha(neon, 0.15)}`, '& .MuiChip-label': { px: 1 } }}
          />
        );
      },
    },
    {
      key: 'actions', label: 'Actions', sortable: false, align: 'right', minWidth: 130,
      render: (company) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Company" arrow>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/companies/${company.id}`); }}
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                }
              }}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManageCompanies && !company.archived_at && (
            <Tooltip title="Edit Company" arrow>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/companies/edit/${company.id}`); }}
                sx={{
                  color: theme.palette.warning.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                  }
                }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canManageCompanies && company.archived_at && (
            <Tooltip title="Restore Company" arrow>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRestore(company); }}
                sx={{ color: theme.palette.success.main, '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.1) } }}>
                <RestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canManageCompanies && !company.archived_at && canDeleteCompany(currentUser, company) && (
            <Tooltip title="Archive Company" arrow>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(company); }}
                sx={{
                  color: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                  }
                }}>
                <ArchiveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (!currentUser || !hasPartnershipAccess) return null;
  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading companies list" size={56} variant="primary" />
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Header
        title="Company Management"
        subtitle="Manage partnerships, sponsors, and suppliers"
        icon={BusinessIcon}
        actionButton={
          canManageCompanies ? (
            <Box sx={{
              display: 'flex',
              gap: 1.2,
              flexWrap: 'wrap',
              justifyContent: { xs: 'stretch', md: 'flex-end' },
              width: '100%',
            }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/companies/add')}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.22)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.34)' },
                  borderRadius: 2,
                  px: 2.5,
                  py: 1.5,
                  minWidth: { xs: '100%', sm: 170 },
                  flex: { xs: '1 1 100%', sm: '0 1 auto' },
                }}
              >
                Add Company
              </Button>
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={handleImportCompaniesClick}
                disabled={importLoading}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.16)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.28)' },
                  borderRadius: 2,
                  px: 2.5,
                  py: 1.5,
                  minWidth: { xs: '100%', sm: 200 },
                  flex: { xs: '1 1 100%', sm: '0 1 auto' },
                }}
              >
                {importLoading ? 'Importing...' : 'Import CSV/Excel'}
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
                disabled={templateLoading}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.16)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.28)' },
                  borderRadius: 2,
                  px: 2.5,
                  py: 1.5,
                  minWidth: { xs: '100%', sm: 210 },
                  flex: { xs: '1 1 100%', sm: '0 1 auto' },
                }}
              >
                {templateLoading ? 'Preparing...' : 'Download Template'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleImportCompaniesFile}
              />
            </Box>
          ) : null
        }
      />

      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
        }}
      />

      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha(SUCCESS, 0.12),
          border: `1px solid ${alpha(SUCCESS, 0.25)}`,
          color: alpha(SUCCESS, 0.95),
          mb: 3,
        }}
      />

      <StatCards stats={statsData} onCardClick={handleStatCardClick} activeIndex={activeStatIndex} />

      {/* Dark glass filter bar */}
      <MotionCard
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.16 }}
        elevation={0}
        sx={{ ...cardShellSx, mb: 2.5 }}
      >
        <Box sx={{ position: 'absolute', inset: 0, background: DOT_GRID, pointerEvents: 'none', zIndex: 0 }} />
        <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterOptions={filterOptions}
            searchPlaceholder="Search by name, description, contact person, assignee, or email..."
          />
        </CardContent>
      </MotionCard>
      {filters.status !== 'archived' && <ListBulkToolbar
        config={COMPANY_BULK_CONFIG}
        currentUser={currentUser}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: COMPANY_BULK_CONFIG.endpoint,
          setItems: setCompanies,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'company(s)',
          refetch: fetchCompanies,
          setError,
        })}
        onDeleteSuccess={handleBulkArchive}
      />}



      <TableList
        columns={columns}
        data={sortedCompanies}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        {...(filters.status !== 'archived' ? bulkTableProps(COMPANY_BULK_CONFIG, currentUser, selectedIds, setSelectedIds) : {})}
        loading={loading}
        emptyState={{ icon: <BusinessIcon />, title: 'No companies found', description: searchTerm || Object.keys(filters).length > 0 ? 'Try adjusting your search or filters.' : 'Start by adding your first company partner.' }}
        onRowClick={(row) => navigate(`/companies/${row.id}`)}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setSelectedCompany(null); }}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        itemType="company"
        itemName={selectedCompany?.name}
        title="Archive Company"
        description="The company will be archived (soft-deleted) and retained in the database for recovery."
        confirmLabel="Archive Company"
        actionVerb="archive"
      />
    </Container>
  );
};

export default CompanyList;
